import React, { useState, useEffect } from 'react';
import { 
  Package, TrendingUp, AlertTriangle, Calendar,
  ShoppingCart, Truck, Clock, BarChart3,
  CheckCircle, XCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import StockLevelChart from './StockLevelChart';
import OrderProposal from './OrderProposal';
import LeadTimeManager from './LeadTimeManager';
import StockAlerts from './StockAlerts';
import EventStockAdjustment from './EventStockAdjustment';
import OptimizationEngine from './OptimizationEngine';
import ABCAnalysis from './ABCAnalysis';
import StockTrendChart from './StockTrendChart';
import AgentCommunicationBus from '../../../services/AgentCommunicationBus';

interface StockItem {
  skuId: string;
  skuName: string;
  category: string;
  currentStock: number;
  optimalStock: number;
  minStock: number;
  maxStock: number;
  dailyDemand: number;
  leadTimeDays: number;
  reorderPoint: number;
  orderQuantity: number;
  stockDays: number;
  status: 'optimal' | 'low' | 'critical' | 'excess';
}

interface OrderSuggestion {
  skuId: string;
  skuName: string;
  currentStock: number;
  suggestedQuantity: number;
  urgency: 'immediate' | 'today' | 'this_week' | 'next_week';
  reason: string;
  estimatedCost: number;
  expectedDelivery: string;
}

const InventoryOptimizerAgent: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [orderSuggestions, setOrderSuggestions] = useState<OrderSuggestion[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [demandForecast, setDemandForecast] = useState<any>(null);
  const [eventAdjustments, setEventAdjustments] = useState<any[]>([]);
  
  // KPI値
  const [stockTurnover, setStockTurnover] = useState(12.5);
  const [stockoutRisk, setStockoutRisk] = useState(8);
  const [excessStock, setExcessStock] = useState(15);
  const [fillRate, setFillRate] = useState(95.2);

  // エージェント登録とメッセージ受信
  useEffect(() => {
    AgentCommunicationBus.register(
      'inventory',
      ['forecast_quantities', 'event_info', 'md_calendar'],
      (message) => {
        console.log('[Inventory] Received message:', message);
        
        // 需要予測データを受信
        if (message.topic === 'forecast_quantities' && message.from === 'demand_forecast') {
          setDemandForecast(message.payload);
          updateStockOptimization(message.payload.forecasts);
        }
        
        // MDカレンダーからイベント情報を受信
        if (message.topic === 'event_info' && message.from === '52week_md') {
          setEventAdjustments(message.payload.events);
        }
      }
    );

    return () => {
      AgentCommunicationBus.unregister('inventory');
    };
  }, []);

  // 初回マウント時のデータ生成とKPI初期設定
  useEffect(() => {
    generateMockData();
    // KPI初期値を設定
    setStockTurnover(12.5 + Math.random() * 2);
    setStockoutRisk(Math.round(5 + Math.random() * 5));
    setExcessStock(Math.round(10 + Math.random() * 10));
    setFillRate(93 + Math.random() * 5);
  }, []);

  // モックデータ生成
  const generateMockData = () => {
    const categories = ['飲料', '菓子', '日配品', '冷凍食品', '生鮮'];
    const items: StockItem[] = [
      { id: 'SKU001', name: 'コカ・コーラ 500ml', cat: '飲料', daily: 50 },
      { id: 'SKU002', name: 'ポテトチップス うすしお', cat: '菓子', daily: 30 },
      { id: 'SKU003', name: '牛乳 1L', cat: '日配品', daily: 40 },
      { id: 'SKU004', name: '冷凍餃子 12個入', cat: '冷凍食品', daily: 20 },
      { id: 'SKU005', name: 'バナナ 1房', cat: '生鮮', daily: 25 },
      { id: 'SKU006', name: 'サントリー天然水 2L', cat: '飲料', daily: 35 },
      { id: 'SKU007', name: 'チョコレート', cat: '菓子', daily: 45 },
      { id: 'SKU008', name: 'ヨーグルト 4個パック', cat: '日配品', daily: 30 },
      { id: 'SKU009', name: '冷凍チャーハン', cat: '冷凍食品', daily: 15 },
      { id: 'SKU010', name: 'トマト 3個入', cat: '生鮮', daily: 20 },
    ].map(item => {
      const dailyDemand = item.daily + Math.floor(Math.random() * 10 - 5);
      const leadTimeDays = item.cat === '生鮮' ? 1 : item.cat === '日配品' ? 2 : 3;
      const safetyStock = dailyDemand * leadTimeDays * 1.5;
      const reorderPoint = dailyDemand * leadTimeDays + safetyStock;
      const currentStock = Math.floor(Math.random() * 500) + 100;
      const optimalStock = dailyDemand * 7; // 1週間分
      const stockDays = Math.floor(currentStock / dailyDemand);
      
      let status: 'optimal' | 'low' | 'critical' | 'excess';
      if (currentStock < reorderPoint * 0.5) status = 'critical';
      else if (currentStock < reorderPoint) status = 'low';
      else if (currentStock > optimalStock * 2) status = 'excess';
      else status = 'optimal';
      
      return {
        skuId: item.id,
        skuName: item.name,
        category: item.cat,
        currentStock,
        optimalStock,
        minStock: Math.floor(safetyStock),
        maxStock: Math.floor(optimalStock * 2),
        dailyDemand,
        leadTimeDays,
        reorderPoint: Math.floor(reorderPoint),
        orderQuantity: Math.floor(optimalStock - currentStock),
        stockDays,
        status
      };
    });
    
    setStockItems(items);
    generateOrderSuggestions(items);
  };

  // 発注提案の生成
  const generateOrderSuggestions = (items: StockItem[]) => {
    const suggestions: OrderSuggestion[] = items
      .filter(item => item.status === 'low' || item.status === 'critical')
      .map(item => {
        const urgency = item.status === 'critical' ? 'immediate' : 
                       item.stockDays < 2 ? 'today' :
                       item.stockDays < 5 ? 'this_week' : 'next_week';
        
        const deliveryDate = new Date();
        deliveryDate.setDate(deliveryDate.getDate() + item.leadTimeDays);
        
        return {
          skuId: item.skuId,
          skuName: item.skuName,
          currentStock: item.currentStock,
          suggestedQuantity: Math.max(0, item.optimalStock - item.currentStock + item.minStock),
          urgency,
          reason: item.status === 'critical' ? '在庫切れ寸前' : 
                 item.stockDays < 3 ? `残り${item.stockDays}日分` : '再発注点到達',
          estimatedCost: Math.floor(Math.random() * 50000 + 10000),
          expectedDelivery: deliveryDate.toISOString().split('T')[0]
        };
      });
    
    setOrderSuggestions(suggestions);
  };

  // 需要予測に基づく最適化更新
  const updateStockOptimization = (forecasts: any[]) => {
    // 予測データを元に在庫を再計算
    const updatedItems = stockItems.map(item => {
      const forecast = forecasts.find(f => f.skuId === item.skuId);
      if (forecast) {
        const adjustedDemand = forecast.quantity / 7; // 週間予測を日次に変換
        const newOptimal = adjustedDemand * 7 * (1 + forecast.confidence / 100);
        return {
          ...item,
          dailyDemand: adjustedDemand,
          optimalStock: Math.floor(newOptimal)
        };
      }
      return item;
    });
    
    setStockItems(updatedItems);
  };

  // 最適化実行
  const runOptimization = () => {
    setIsOptimizing(true);
    
    // 棚割りエージェントに在庫情報を送信
    AgentCommunicationBus.send({
      from: 'inventory',
      to: 'planogram',
      type: 'update',
      topic: 'stock_levels',
      payload: {
        stockItems: stockItems.map(item => ({
          skuId: item.skuId,
          stockLevel: item.currentStock,
          status: item.status,
          recommendation: item.status === 'excess' ? 'promote' : 
                         item.status === 'low' ? 'deemphasize' : 'maintain'
        }))
      },
      priority: 'medium'
    });
    
    // チラシエージェントに在庫可用性を送信
    AgentCommunicationBus.send({
      from: 'inventory',
      to: 'flyer',
      type: 'notification',
      topic: 'stock_availability',
      payload: {
        availableItems: stockItems.filter(i => i.status === 'optimal' || i.status === 'excess'),
        restrictedItems: stockItems.filter(i => i.status === 'critical' || i.status === 'low')
      },
      priority: 'high'
    });
    
    setTimeout(() => {
      setIsOptimizing(false);
      // KPI更新
      const newTurnover = 11 + Math.random() * 3;
      const newRisk = Math.round(Math.max(3, stockoutRisk - Math.random() * 3));
      const newFillRate = Math.min(98, fillRate + Math.random() * 2);
      const newExcess = Math.round(Math.max(5, excessStock - Math.random() * 3));
      
      setStockTurnover(newTurnover);
      setStockoutRisk(newRisk);
      setFillRate(newFillRate);
      setExcessStock(newExcess);
      
      // 最適化実行アクション
      console.log('📦 在庫最適化を実行しました！', {
        turnover: newTurnover,
        risk: newRisk,
        excess: newExcess,
        fillRate: newFillRate
      });
      alert(`✅ 在庫最適化を実行しました！\n\n最適化結果:\n- 在庫回転率: ${newTurnover.toFixed(1)}回\n- 欠品リスク削減: ${(stockoutRisk - newRisk).toFixed(1)}%\n- 過剰在庫削減: ${(excessStock - newExcess).toFixed(1)}%\n- 充足率: ${newFillRate.toFixed(1)}%\n- 最適化SKU数: ${stockItems.length}件`);
    }, 2000);
  };

  const tabs = [
    { id: 'overview', name: '概要', icon: Package },
    { id: 'optimization', name: '最適化', icon: TrendingUp },
    { id: 'abc', name: 'ABC分析', icon: BarChart3 },
    { id: 'trend', name: '在庫推移', icon: TrendingUp },
    { id: 'orders', name: '発注提案', icon: ShoppingCart },
    { id: 'leadtime', name: 'リードタイム', icon: Truck },
    { id: 'alerts', name: 'アラート', icon: AlertTriangle },
    { id: 'events', name: 'イベント調整', icon: Calendar },
  ];

  const categories = ['all', '飲料', '菓子', '日配品', '冷凍食品', '生鮮'];

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4">
        {/* コンパクトなヘッダー */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold" style={{ color: colors.text.primary }}>
                在庫最適化エージェント
              </h2>
              <span className="text-sm" style={{ color: colors.text.secondary }}>
                需要予測とリードタイムから適正在庫・発注量を自動提案
              </span>
            </div>
            <Button variant="primary" size="small" onClick={runOptimization} disabled={isOptimizing}>
              <RefreshCw size={14} className={isOptimizing ? 'animate-spin' : ''} />
              {isOptimizing ? '最適化中...' : stockItems.length === 0 ? '在庫分析' : '再最適化'}
            </Button>
          </div>
        </div>

        {/* コンパクトなKPIカード */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <KPICard
            title="在庫回転率"
            value={`${stockTurnover.toFixed(1)}回`}
            change="+0.8回"
            icon={<TrendingUp />}
            trend="up"
          />
          <KPICard
            title="欠品リスク"
            value={`${stockoutRisk}品`}
            change="-2品"
            icon={<AlertTriangle />}
            trend="down"
          />
          <KPICard
            title="過剰在庫"
            value={`${excessStock}品`}
            change="-3品"
            icon={<Package />}
            trend="down"
          />
          <KPICard
            title="充足率"
            value={`${fillRate.toFixed(1)}%`}
            change="+1.2%"
            icon={<CheckCircle />}
            trend="up"
          />
        </div>

        {/* タブナビゲーション */}
        <div className="flex gap-1 mb-3 border-b" style={{ borderColor: colors.ui.border }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium border-b-2 transition-all ${
                  activeTab === tab.id ? 'border-current' : 'border-transparent'
                }`}
                style={{ 
                  color: activeTab === tab.id ? colors.support.success : colors.text.secondary,
                  borderColor: activeTab === tab.id ? colors.support.success : 'transparent'
                }}
              >
                <Icon size={14} />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* タブコンテンツ */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <>
              {/* カテゴリフィルター */}
              <div className="flex items-center gap-2">
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat === 'all' ? '全カテゴリ' : cat}
                  </Button>
                ))}
              </div>

              {/* 在庫レベルチャート */}
              <StockLevelChart stockItems={stockItems.filter(item => 
                selectedCategory === 'all' || item.category === selectedCategory
              )} />

              {/* 在庫状況テーブル（コンパクト版） */}
              <Card className="overflow-hidden">
                <div className="p-2 border-b" style={{ borderColor: colors.ui.border }}>
                  <h3 className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                    在庫状況一覧
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b" style={{ borderColor: colors.ui.border }}>
                      <tr style={{ backgroundColor: colors.ui.bgSecondary }}>
                        <th className="text-left p-2 text-xs font-medium">SKU</th>
                        <th className="text-center p-2 text-xs font-medium">現在庫</th>
                        <th className="text-center p-2 text-xs font-medium">適正在庫</th>
                        <th className="text-center p-2 text-xs font-medium">日次需要</th>
                        <th className="text-center p-2 text-xs font-medium">在庫日数</th>
                        <th className="text-center p-2 text-xs font-medium">状態</th>
                        <th className="text-center p-2 text-xs font-medium">推奨アクション</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockItems
                        .filter(item => selectedCategory === 'all' || item.category === selectedCategory)
                        .slice(0, 15)
                        .map(item => (
                          <tr key={item.skuId} className="border-b hover:bg-gray-50" style={{ borderColor: colors.ui.border }}>
                            <td className="p-2">
                              <div>
                                <div className="text-xs font-medium">{item.skuId}</div>
                                <div className="text-xs text-gray-500">{item.skuName}</div>
                              </div>
                            </td>
                            <td className="p-2 text-center text-xs">
                              <span className={`font-medium ${
                                item.currentStock < item.minStock ? 'text-red-600' : ''
                              }`}>
                                {item.currentStock}
                              </span>
                            </td>
                            <td className="p-2 text-center text-xs">{item.optimalStock}</td>
                            <td className="p-2 text-center text-xs">{item.dailyDemand}</td>
                            <td className="p-2 text-center text-xs">
                              <div className="flex items-center justify-center gap-1">
                                <Clock size={12} className="text-gray-400" />
                                <span>{item.stockDays}日</span>
                              </div>
                            </td>
                            <td className="p-2 text-center">
                              <Badge 
                                variant={
                                  item.status === 'critical' ? 'error' :
                                  item.status === 'low' ? 'warning' :
                                  item.status === 'excess' ? 'secondary' : 'success'
                                }
                                size="sm"
                              >
                                {item.status === 'critical' ? '危険' :
                                 item.status === 'low' ? '低在庫' :
                                 item.status === 'excess' ? '過剰' : '適正'}
                              </Badge>
                            </td>
                            <td className="p-2 text-center">
                              {item.status === 'critical' || item.status === 'low' ? (
                                <Button size="small" variant="primary">
                                  発注
                                </Button>
                              ) : item.status === 'excess' ? (
                                <Button size="small" variant="secondary">
                                  販促
                                </Button>
                              ) : (
                                <span className="text-sm text-gray-500">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {activeTab === 'optimization' && (
            <OptimizationEngine />
          )}

          {activeTab === 'abc' && (
            <ABCAnalysis />
          )}

          {activeTab === 'trend' && (
            <StockTrendChart />
          )}

          {activeTab === 'orders' && (
            <OrderProposal suggestions={orderSuggestions} stockItems={stockItems} />
          )}

          {activeTab === 'leadtime' && (
            <LeadTimeManager stockItems={stockItems} />
          )}

          {activeTab === 'alerts' && (
            <StockAlerts stockItems={stockItems} />
          )}

          {activeTab === 'events' && (
            <EventStockAdjustment stockItems={stockItems} eventAdjustments={eventAdjustments} />
          )}
        </div>
      </div>
    </div>
  );
};

// KPIカードコンポーネント
interface KPICardProps {
  title: string;
  value: string;
  change: string;
  icon: React.ReactNode;
  trend: 'up' | 'down' | 'neutral';
}

const KPICard: React.FC<KPICardProps> = ({ title, value, change, icon, trend }) => {
  const trendColor = trend === 'up' ? colors.support.success : 
                     trend === 'down' ? colors.status.error : 
                     colors.text.secondary;
  
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded" style={{ backgroundColor: colors.support.success + '20' }}>
            {React.isValidElement(icon) ? React.cloneElement(icon, { size: 14, style: { color: colors.support.success } }) : icon}
          </div>
          <div className="text-xs" style={{ color: colors.text.secondary }}>
            {title}
          </div>
        </div>
        <span className="text-xs font-medium" style={{ color: trendColor }}>
          {change}
        </span>
      </div>
      <div className="text-xl font-bold" style={{ color: colors.text.primary }}>
        {value}
      </div>
    </Card>
  );
};

export default InventoryOptimizerAgent;
